/**
 * Upload… on the asset page (1.2.2), for asset.upload on an asset with media (a placeholder keeps its Upload media).
 * "What is this file?":
 *   A new version of this asset   (the default) version N+1 becomes the current version; the metadata, class,
 *                                 collections and shares stay with the asset. The further masters of version N stay
 *                                 with it (they match the old edit), and the dialog names them. Same media kind only.
 *   A new master of version N     video and audio assets: the Masters tab's Add master… (Variant, default Programme).
 */
import { useState } from 'react';
import { Layers, Upload, Files } from 'lucide-react';
import { andList, fileMediaKind, type AssetDetail, type MasterConflict, type MasterDTO, type MasterVariantDTO } from '@prism/contracts';
import { useAddMaster, useMasterVariants, useMe, useVersionUpload } from '../api/hooks';
import { FileChooser, MasterChoiceFields, startAddMaster, useMastersOf, useMasterChoice } from '../pages/MastersTab';
import { Modal } from './Modal';
import { toast } from './Toasts';

/** The names of a version's further masters, for "Textless and M&E stay with version 3": a Programme master by its format. */
export function furtherMasterNames(masters: Array<Pick<MasterDTO, 'primary' | 'variant' | 'format'>>): string[] {
  const primary = masters.find((m) => m.primary);
  const names = masters.filter((m) => !m.primary).map((m) => (primary && m.variant.id === primary.variant.id ? `${m.variant.name} ${m.format.formatClass}` : m.variant.name));
  return [...new Set(names)];
}

/** The browser's idea of a file's media kind (empty for MXF and the like: the server decides then). */
const kindOfFile = (f: File) => fileMediaKind(f.type || null);

export function AssetUploadButton({ asset, onOpenTab }: { asset: AssetDetail; onOpenTab?: (tab: 'masters') => void }) {
  const me = useMe().data;
  const [open, setOpen] = useState(false);
  if (!me?.permissions.includes('asset.upload') || asset.deleted || asset.status === 'placeholder' || asset.versionNumber == null || asset.mediaKind === 'graphic') return null;
  return (
    <>
      <button className="btn sm" onClick={() => setOpen(true)} data-testid="asset-upload" title="A new version of this asset, or a new master"><Upload size={14} /> Upload…</button>
      {open && <AssetUploadDialog asset={asset} onClose={() => setOpen(false)} onOpenTab={onOpenTab} />}
    </>
  );
}

function AssetUploadDialog({ asset, onClose, onOpenTab }: { asset: AssetDetail; onClose: () => void; onOpenTab?: (tab: 'masters') => void }) {
  const hasMasters = asset.mediaKind === 'video' || asset.mediaKind === 'audio';
  const [what, setWhat] = useState<'version' | 'master'>('version');
  const [file, setFile] = useState<File | null>(null);
  const { masters } = useMastersOf(asset);
  const variants: MasterVariantDTO[] = useMasterVariants(hasMasters).data?.items ?? [];
  const choice = useMasterChoice(variants);
  const versions = useVersionUpload(asset.id);
  const addMaster = useAddMaster(asset.id);
  const n = asset.versionNumber!;
  const stay = furtherMasterNames(masters);
  const fileKind = file ? kindOfFile(file) : null;
  const wrongKind = what === 'version' && file && fileKind && asset.mediaKind && fileKind !== asset.mediaKind ? fileKind : null;
  const start = () => {
    if (!file) return;
    if (what === 'version') {
      versions.add(file, { assetCode: asset.assetCode, title: asset.title, expectedVersion: n });
      toast(`Uploading ${file.name} as version ${n + 1} of ${asset.assetCode}`);
    } else if (choice.variant) {
      startAddMaster(addMaster.add, asset, file, choice.variant, choice.onConflict as MasterConflict);
      onOpenTab?.('masters');
    }
    onClose();
  };
  return (
    <Modal title="What is this file?" onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>Cancel</button>
      <button className="btn primary" disabled={!file || Boolean(wrongKind) || (what === 'master' && !choice.variant)} onClick={start} data-testid="asset-upload-start">
        <Upload size={14} /> {what === 'version' ? `Upload as version ${n + 1}` : 'Upload the master'}
      </button>
    </>}>
      <div className="choice-list" role="radiogroup" aria-label="What is this file?" data-testid="asset-upload-choices">
        <label className={`choice ${what === 'version' ? 'on' : ''}`}>
          <input type="radio" name="asset-upload-what" checked={what === 'version'} onChange={() => setWhat('version')} data-testid="asset-upload-version" />
          <span>
            <strong><Files size={13} style={{ verticalAlign: -2 }} /> A new version of this asset</strong>
            <span className="muted">It becomes version {n + 1} and the current version. The metadata, class, collections and shares stay with the asset; version {n} stays as an earlier version.</span>
          </span>
        </label>
        {hasMasters && (
          <label className={`choice ${what === 'master' ? 'on' : ''}`}>
            <input type="radio" name="asset-upload-what" checked={what === 'master'} onChange={() => setWhat('master')} data-testid="asset-upload-master" />
            <span>
              <strong><Layers size={13} style={{ verticalAlign: -2 }} /> A new master of version {n}</strong>
              <span className="muted">The same content in another resolution or variant (Textless, M&amp;E, UHD…), as Add master… on the Masters tab.</span>
            </span>
          </label>
        )}
      </div>
      {what === 'version' && stay.length > 0 && (
        <p className="banner warning" style={{ margin: '0 0 4px' }} data-testid="asset-upload-masters-notice">
          {andList(stay)} {stay.length === 1 ? 'stays' : 'stay'} with version {n}, which {stay.length === 1 ? 'it matches' : 'they match'}. Add {stay.length === 1 ? 'it' : 'them'} again for version {n + 1} after this upload.
        </p>
      )}
      <FileChooser file={file} onFile={setFile} testId="asset-upload" />
      {wrongKind && (
        <p className="error-text" style={{ marginTop: -4 }} data-testid="asset-upload-kind">
          This asset is {asset.mediaKind === 'image' ? 'an image' : asset.mediaKind} and the file is {wrongKind === 'image' ? 'an image' : wrongKind}: a new version must be the same kind of media.
        </p>
      )}
      {what === 'master' && <MasterChoiceFields variants={variants} choice={choice} />}
    </Modal>
  );
}
